import React, { useState } from 'react';
import { BatteryFull, BatteryMedium, BatteryLow, BatteryWarning, CpuIcon, ThermometerIcon, WifiIcon, ClockIcon } from "lucide-react";
import DeviceHistoryModal from './DeviceHistoryModal';
import { API_BASE } from '../config'; // ← use shared base URL

interface HistoryEntry {
  id: number;
  timestamp: string;   // modal expects "timestamp"
  metricType: string;  // modal expects "metricType"
  value: string;
}

interface DeviceProps {
    name: string;
    wifiConnected?: boolean;
    batteryCharge?: number;
    temperature?: number;
    firmwareVersion?: string;
    latencyMs?: number;
    isAdmin?: boolean;
    stadium?: string;
    mode?: string;
    rssi?: number | null;
}

const STADIUM_RENDERS: Record<string, string> = {
    marvel: "/marvel render.png",
};

function DeviceComponent({
    name,
    wifiConnected,
    batteryCharge,
    temperature,
    firmwareVersion,
    latencyMs = -1,
    isAdmin = false,
    stadium,
    mode,
    rssi,
}: DeviceProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const handleClick = async () => {
        setIsModalOpen(true);
        setIsLoading(true);
        const data = await fetchHistory();
        setHistory(data.logs);
        setHasMore(data.hasMore);
        setIsLoading(false);
    };

    const handleLoadMore = async (lastId: number) => {
        const data = await fetchHistory(lastId);
        setHistory(prev => [...prev, ...data.logs]);
        setHasMore(data.hasMore);
    };

    const fetchHistory = async (lastId?: number) => {
        try {
            const token = localStorage.getItem('token') || '';
            const url = new URL(`${API_BASE}/api/device/${encodeURIComponent(name)}/history`);
            url.searchParams.set('hours', '3');
            url.searchParams.set('page_size', '20');
            if (lastId) url.searchParams.set('last_id', String(lastId));

            const response = await fetch(url.toString(), {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Map API fields (id, ts, metric, value) -> component shape
            const logs: HistoryEntry[] = (data.logs || []).map((l: any) => ({
                id: l.id,
                timestamp: l.ts,
                metricType: l.metric,
                value: l.value,
            }));

            return {
                logs,
                hasMore: !!data.hasMore
            };
        } catch (error) {
            console.error('Error fetching device history:', error);
            return { logs: [], hasMore: false };
        }
    };

    const pct = batteryCharge ?? 0;
    const batteryColor = pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-yellow-400' : 'bg-red-500';
    const batteryIconColor = pct > 50 ? 'text-green-500' : pct > 20 ? 'text-yellow-400' : 'text-red-500';
    const BatteryIcon = pct > 75 ? BatteryFull : pct > 50 ? BatteryMedium : pct > 20 ? BatteryLow : BatteryWarning;

    // RSSI traffic-light colours — only meaningful when mode is "4g"
    const rssiColor =
        rssi == null ? null
        : rssi >= -70 ? "bg-green-500"
        : rssi >= -85 ? "bg-yellow-400"
        :               "bg-red-500";

    const rssiLabel =
        rssi == null ? null
        : rssi >= -70 ? "Good"
        : rssi >= -85 ? "OK"
        :               "Weak";

    return (
        <>
            <div
                className="flex flex-col items-center text-center cursor-pointer hover:shadow-lg transition-shadow rounded-lg p-2 space-y-3"
                onClick={handleClick}
            >
                {/* Device name */}
                <h3 className="text-lg font-bold text-slate-800">{name}</h3>

                {/* Connection status */}
                <div className="flex items-center space-x-1">
                    <WifiIcon className={`h-5 w-5 ${wifiConnected ? 'text-green-500' : 'text-red-500'}`} />
                    <span className={`font-medium ${wifiConnected ? 'text-green-600' : 'text-red-500'}`}>
                        {wifiConnected ? 'Connected' : 'Disconnected'}
                    </span>
                </div>

                {/* Device render image */}
                <img src={STADIUM_RENDERS[stadium ?? ""] ?? "/render.png"} alt="Device" className="w-48 h-auto object-contain" />

                {/* Battery bar */}
                <div className="w-full px-2 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-1">
                            <BatteryIcon className={`h-4 w-4 ${batteryIconColor}`} />
                            <span>Battery</span>
                        </div>
                        <span className="font-medium">{batteryCharge}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className={`h-2.5 rounded-full ${batteryColor}`}
                            style={{ width: `${batteryCharge}%` }}
                        ></div>
                    </div>
                </div>

                {/* Admin-only: temperature */}
                {isAdmin && (
                    <div className="flex items-center space-x-1 text-sm">
                        <ThermometerIcon className="h-4 w-4" />
                        <span>Temperature: {temperature}°C</span>
                    </div>
                )}

                {/* Latency */}
                {latencyMs >= 0 && (
                    <div className="flex items-center space-x-1 text-sm">
                        <ClockIcon className="h-4 w-4" />
                        <span>RTT: {latencyMs.toFixed(1)} ms</span>
                    </div>
                )}

                {/* Network mode badge */}
                {mode && (
                    <div className="flex items-center space-x-1 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide
                            ${mode === "4g"
                                ? "bg-blue-100 text-blue-700 border border-blue-300"
                                : "bg-gray-100 text-gray-600 border border-gray-300"}`}>
                            {mode === "4g" ? "4G" : "Wi-Fi"}
                        </span>
                    </div>
                )}

                {/* RSSI traffic light — shown whenever rssi is known */}
                {rssi != null && rssiColor && (
                    <div className="flex items-center space-x-1.5 text-sm">
                        <span
                            className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${rssiColor}`}
                            title={`RSSI: ${rssi} dBm — ${rssiLabel}`}
                        />
                        <span className="text-slate-600">
                            RSSI: <span className="font-medium">{rssi} dBm</span>
                            <span className="text-xs ml-1 text-slate-400">({rssiLabel})</span>
                        </span>
                    </div>
                )}

                {/* Firmware */}
                <p className="text-sm text-slate-600">
                    <CpuIcon className="inline h-4 w-4 mr-1" />
                    Firmware: {firmwareVersion}
                </p>
            </div>

            <DeviceHistoryModal
                deviceName={name}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                history={history}
                isLoading={isLoading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
            />
        </>
    );
}

export default DeviceComponent;
